import type {
  AutocompleteGroup,
  AutocompleteOption,
} from "@/components/ui/molecules/autocomplete/Autocomplete.type";
import { derive } from "@/lib/derive-zustand";
import { useStore } from "zustand";
import useSegmentStore from "./segment.slice";
import type { Segment } from "./segment.slice";

/**
 * Creates a lookup Map from segment ID to segment display properties.
 * Automatically updates when segments change.
 * @returns {Map<string, {color: string, name: string, isVisible: boolean}>} Map of segment ID to color, name, and visibility
 */
const deriveSegmentLookup = derive((get) => {
  const { segments } = get(useSegmentStore);

  const segmentLookup = new Map<
    string,
    { color: string; name: string; isVisible: boolean }
  >();

  for (const segment of segments) {
    segmentLookup.set(segment.id, {
      color: segment.hexColor,
      name: segment.name,
      isVisible: segment.isVisible,
    });
  }

  return segmentLookup;
});

/**
 * Creates sorted segments array, automatically sorted by order and visibility.
 * Visible segments are sorted by their order field.
 * Invisible segments are moved to the bottom of the array.
 * @returns {Segment[]} Array of segments sorted by order and visibility
 */
const deriveSortedSegments = derive((get): Segment[] => {
  const { segments } = get(useSegmentStore);
  return Array.from(segments).sort((a, b) => {
    // If both segments have the same visibility, sort by order
    if (a.isVisible === b.isVisible) {
      return a.order - b.order;
    }

    // If visibility differs, visible segments come first
    return a.isVisible ? -1 : 1;
  });
});

/**
 * Creates autocomplete options from key message groups and filters out groups without keyMessageItems.
 * @returns {AutocompleteGroup<AutocompleteOption>[]} Autocomplete options for key messages
 */
const deriveKeyMessageOptions = derive(
  (get): AutocompleteGroup<AutocompleteOption>[] => {
    const { keyMessagesAndGroups } = get(useSegmentStore);

    return keyMessagesAndGroups.reduce((acc, group) => {
      if (group.keyMessageItems && group.keyMessageItems.length > 0) {
        acc.push({
          label: group.name,
          options: group.keyMessageItems.map((item) => ({
            value: item.id,
            label: item.name,
          })),
        });
      }
      return acc;
    }, [] as AutocompleteGroup<AutocompleteOption>[]);
  },
);

export const useSegmentLookup = () => useStore(deriveSegmentLookup);
export const useSortedSegments = () =>
  useStore(deriveSortedSegments) as Segment[];
export const useKeyMessageOptions = () =>
  useStore(deriveKeyMessageOptions) as AutocompleteGroup<AutocompleteOption>[];
